import { BlockDefinition } from "@softr-io/blocks-sdk";
import { assert, isInModal } from "@softr/utils";
import * as v from "valibot";

const BlockDefinitionSchema = v.pipe(
    // Only a sanity check. Fully specifying the BlockDefinition schema is not feasible
    // at the moment
    v.looseObject({ id: v.string(), hrid: v.string() }),
    // Casting to BlockDefinition because we're missing the full BlockDefinition schema
    v.transform((obj) => obj as BlockDefinition),
);
/**
 * https://github.com/softr-io/softr-platform/blob/master/src/main/java/io/softr/platform/application/data/resource/PageBlocksResource.java
 */
const PageDefinitionSchema = v.object({
    blocks: v.pipe(v.array(BlockDefinitionSchema), v.readonly()),
    pageId: v.string(),
    headerCustomCode: v.string(),
    footerCustomCode: v.string(),
    /**
     * https://github.com/softr-io/softr-platform/blame/master/src/main/java/io/softr/platform/application/data/resource/PageMetadata.java#L31-L34
     */
    metadata: v.object({
        title: v.string(),
        description: v.string(),
        slug: v.union([v.string(), v.null()]),
        socialTitle: v.union([v.string(), v.null()]),
        socialDescription: v.union([v.string(), v.null()]),
        socialImage: v.union([v.string(), v.null()]),
        // Resolved by the platform with the same rules as the server-rendered page.
        // Null when Softr emits no canonical, undefined when the platform doesn't return it yet.
        canonicalUrl: v.optional(v.union([v.string(), v.null()])),
    }),
});
export type PageDefinition = v.InferOutput<typeof PageDefinitionSchema>;
export type PageMetadata = PageDefinition["metadata"];
const fetchPageDefinitionCache = new Map<string, Promise<PageDefinition>>();

type FetchPageDefinitionOpts = Pick<RequestInit, "priority">;
export async function fetchPageDefinition(
    pageURL: string,
    opts: FetchPageDefinitionOpts = {},
): Promise<PageDefinition> {
    pageURL = normalizeURLForPageFetch(pageURL);
    const cached = fetchPageDefinitionCache.get(pageURL);
    if (cached) {
        return cached;
    }
    const promise = _fetchPageBlocks(pageURL, opts);
    fetchPageDefinitionCache.set(pageURL, promise);
    return promise;
}
fetchPageDefinition.initializeCache = (initialHref: string, initialPageDefinition: PageDefinition) => {
    initialHref = normalizeURLForPageFetch(initialHref);
    fetchPageDefinitionCache.set(initialHref, Promise.resolve(initialPageDefinition));
};
/**
 * Drops a single page from the cache so the next {@link fetchPageDefinition} hits the network.
 * Only the given page: clearing the whole cache would make every SPALink prefetch fetch again.
 */
fetchPageDefinition.invalidate = (pageURL: string) => {
    fetchPageDefinitionCache.delete(normalizeURLForPageFetch(pageURL));
};

/**
 * Whether two URLs would be served by the same page definition. Unlike comparing
 * hrefs, this ignores the hash, modal params and the other search params that
 * don't select a page.
 */
export function isSamePageFetchTarget(a: string, b: string): boolean {
    return normalizeURLForPageFetch(a) === normalizeURLForPageFetch(b);
}

function normalizeURLForPageFetch(url: string): string {
    const u = new URL(url);
    let result = u.pathname;
    for (const [key, value] of u.searchParams.entries()) {
        if (key.toLowerCase() === "recordid") {
            result += `?recordId=${value}`;
            break;
        }
    }
    return result;
}

async function _fetchPageBlocks(path: string, opts: RequestInit): Promise<PageDefinition> {
    assert(path.startsWith("/"), 'Path must start with a "/"');
    const response = await fetch(`/softr-system/pages${path}`, opts);
    assert(response.ok, `Failed to fetch page definition for ${path}: ${response.status} ${response.statusText}`);
    const defs = v.parse(PageDefinitionSchema, await response.json());
    if (isInModal()) {
        return {
            ...defs,
            blocks: defs.blocks.filter((block) => block.name !== "navigation"),
        };
    } else {
        return defs;
    }
}
